// Ett gemensamt ställe för besökarens integritetsval. Webben visar en
// cookieruta, apparna ett appanpassat val – men båda sparar och läser här.

export type ConsentCategories = {
  /** Alltid på: inloggning, säkerhet, och själva valet. */
  necessary: true;
  /** Anonym statistik om hur tjänsten används. */
  analytics: boolean;
  /** Marknadsföring och riktade utskick. Avstängt som standard. */
  marketing: boolean;
};

export type StoredConsent = ConsentCategories & {
  date: string;
  /** Var valet gjordes: "web", "ios" eller "android". */
  platform?: string;
  /** Versionen av vårt integritetsval – höjs när kategorierna ändras. */
  version?: number;
};

/** Höj när kategorierna ändras så att valet frågas om på nytt. */
export const CONSENT_VERSION = 2;

export const STORAGE_KEY = "sellavo-cookie-consent";
const LEGACY_KEYS = ["saljavo-cookie-consent"];

export const CONSENT_CHANGED_EVENT = "sellavo:consent-changed";

function parse(raw: string | null): StoredConsent | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<StoredConsent>;
    if (typeof value !== "object" || value === null) return null;
    return {
      necessary: true,
      analytics: Boolean(value.analytics),
      // Äldre val saknar marknadsföring – då gäller nej.
      marketing: Boolean(value.marketing),
      date: typeof value.date === "string" ? value.date : new Date().toISOString(),
      ...(value.platform ? { platform: value.platform } : {}),
      version: typeof value.version === "number" ? value.version : 1,
    };
  } catch {
    return null;
  }
}

function readCookie(): StoredConsent | null {
  try {
    const cookies = document.cookie.split("; ");
    for (const key of [STORAGE_KEY, ...LEGACY_KEYS]) {
      const match = cookies.find((c) => c.startsWith(`${key}=`));
      if (match) return parse(decodeURIComponent(match.slice(key.length + 1)));
    }
  } catch {
    /* ignoreras */
  }
  return null;
}

/** Sparat val, eller null när besökaren inte svarat än. */
export function readConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    for (const key of [STORAGE_KEY, ...LEGACY_KEYS]) {
      const found = parse(window.localStorage.getItem(key));
      if (found) return found;
    }
  } catch {
    /* blockerad lagring – cookien nedan får ta över */
  }
  return readCookie();
}

/** True när vi behöver fråga (inget val, eller ett val från en äldre version). */
export function needsConsentChoice(): boolean {
  const existing = readConsent();
  if (!existing) return true;
  return (existing.version ?? 1) < CONSENT_VERSION;
}

export function hasAnalyticsConsent(): boolean {
  return Boolean(readConsent()?.analytics);
}

export function hasMarketingConsent(): boolean {
  return Boolean(readConsent()?.marketing);
}

/** Sparar valet lokalt (localStorage + cookie som reserv) och meddelar appen. */
export function writeConsent(
  next: Omit<StoredConsent, "necessary" | "date" | "version"> & { date?: string },
): StoredConsent {
  const consent: StoredConsent = {
    necessary: true,
    analytics: Boolean(next.analytics),
    marketing: Boolean(next.marketing),
    date: next.date ?? new Date().toISOString(),
    ...(next.platform ? { platform: next.platform } : {}),
    version: CONSENT_VERSION,
  };
  const value = JSON.stringify(consent);
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* ignoreras */
  }
  try {
    document.cookie = `${STORAGE_KEY}=${encodeURIComponent(value)}; path=/; max-age=${
      60 * 60 * 24 * 365
    }; SameSite=Lax`;
  } catch {
    /* ignoreras */
  }
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT, { detail: consent }));
  } catch {
    /* ignoreras */
  }
  return consent;
}

/** Öppnar integritetsvalet igen, oavsett om vi är i webben eller appen. */
export function openConsentSettings() {
  window.dispatchEvent(new CustomEvent("sellavo:open-cookie-settings"));
}
